import { parse } from 'query-string';
import { getLang } from '../../../../Utils/locale';
import { getDurationNumber, getDurationString } from '../../../../Utils/utils';
import { BookingPriceBreakdown, SupportedLanguage } from '../../../../Utils/graphql-sdk';
import { selecetedShopIdActionTypes } from '../../../../redusers/selected-shop-id-reducer';

const initialDuration = Number(parse(window.location.search).duration);
const lang = getLang(parse(window.location.search).lang);
const shopId = parse(window.location.search).shopId;

const supportedLanguages = Object.values(SupportedLanguage).map((value) => {
  return String(value).toLowerCase();
});

function isSupportedLanguage(string: string): string is SupportedLanguage {
  return supportedLanguages.includes(string.toLowerCase());
}

export enum bookingActionTypes {
  UPDATE_BOOKING = 'BOOKING_UPDATE_BOOKING',
  UPDATE_BOAT_COUNT = 'BOOKING_UPDATE_BOAT_COUNT',
  UPDATE_DURATION = 'BOOKING_UPDATE_DURATION',
  UPDATE_PICKUP_LOCATION = 'BOOKING_UPDATE_PICKUP_LOCATION',
  UPDATE_DROPOFF_LOCATION = 'BOOKING_UPDATE_DROPOFF_LOCATION',
  UPDATE_DATE = 'BOOKING_UPDATE_DATE',
  UPDATE_START_TIME = 'BOOKING_UPDATE_START_TIME',
  UPDATE_END_TIME = 'BOOKING_UPDATE_END_TIME',
  UPDATE_PRICE_MODEL_ID = 'BOOKING_UPDATE_PRICE_MODEL_ID',
  UPDATE_CANCELLATION_POLICY = 'BOOKING_UPDATE_CANCELLATION_POLICY',
}

interface BookingUpdateBookingAction {
  type: bookingActionTypes.UPDATE_BOOKING;
  payload: Partial<BookingReducerState>;
}

interface BookingUpdateBoatCountAction {
  type: bookingActionTypes.UPDATE_BOAT_COUNT;
  payload: number;
  keepTimeslot: boolean;
}

interface BookingUpdateDurationAction {
  type: bookingActionTypes.UPDATE_DURATION;
  payload: number;
  keepTimeslot: boolean;
}

interface BookingUpdatePickupLocationAction {
  type: bookingActionTypes.UPDATE_PICKUP_LOCATION;
  payload: string | null;
  keepTimeslot: boolean;
}

interface BookingUpdateDropoffLocationAction {
  type: bookingActionTypes.UPDATE_DROPOFF_LOCATION;
  payload: string | null;
  keepTimeslot: boolean;
}

interface BookingUpdateDateAction {
  type: bookingActionTypes.UPDATE_DATE;
  payload: string;
}

interface BookingUpdateStartTimeAction {
  type: bookingActionTypes.UPDATE_START_TIME;
  payload: string;
}

interface BookingUpdateEndTimeAction {
  type: bookingActionTypes.UPDATE_END_TIME;
  payload: string;
}

interface BookingUpdatePriceModelAction {
  type: bookingActionTypes.UPDATE_PRICE_MODEL_ID;
  payload: string;
}

interface BookingCancellationPolicyAction {
  type: bookingActionTypes.UPDATE_CANCELLATION_POLICY;
  payload: string;
}

interface SelecetedShopIdAction {
  type: selecetedShopIdActionTypes.SELECTED_SHOP_ID_UPDATE;
  payload: string;
}

// combine all the action types into one type
export type BookingReducerAction =
  | BookingUpdateBookingAction
  | BookingUpdateBoatCountAction
  | BookingUpdateDurationAction
  | BookingUpdatePickupLocationAction
  | BookingUpdateDropoffLocationAction
  | BookingUpdateDateAction
  | BookingUpdateStartTimeAction
  | BookingUpdateEndTimeAction
  | BookingUpdatePriceModelAction
  | BookingCancellationPolicyAction
  | SelecetedShopIdAction;

export interface BookingReducerState {
  id: string | null;
  shopId: string | null;
  boatCount: number;
  duration: number;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  lang: SupportedLanguage;
  priceModelId: string | null;
  cancellationPolicyId: string | null;
  reservationId: string | null;
  pickupLocationId: string | null;
  dropoffLocationId: string | null;
  modifiedDate: string | null;
  reservedTimeslotExpires: string | null;
  customerId: string | null;
  priceBreakdown?: BookingPriceBreakdown | null;
}

export const bookingReducerInitialState: BookingReducerState = {
  id: null,
  shopId: typeof shopId === 'string' ? shopId : null,
  boatCount: 1,
  duration: initialDuration || 2,
  date: null,
  startTime: null,
  endTime: null,
  lang: isSupportedLanguage(lang) ? lang : SupportedLanguage.En,
  priceModelId: null,
  cancellationPolicyId: null,
  reservationId: null,
  pickupLocationId: null,
  dropoffLocationId: null,
  modifiedDate: null,
  reservedTimeslotExpires: null,
  customerId: null,
};

const bookingReducer = (
  state: BookingReducerState,
  action: BookingReducerAction
): BookingReducerState => {
  switch (action.type) {
    case bookingActionTypes.UPDATE_BOOKING: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case bookingActionTypes.UPDATE_BOAT_COUNT: {
      return {
        ...state,
        ...(action.keepTimeslot
          ? {}
          : {
              date: null,
              startTime: null,
              endTime: null,
            }),
        boatCount: action.payload,
      };
    }
    case bookingActionTypes.UPDATE_DURATION: {
      return {
        ...state,
        ...(action.keepTimeslot
          ? {
              endTime: getDurationString(
                getDurationNumber(state.startTime ?? '') + Number(action.payload)
              ),
            }
          : {
              date: null,
              startTime: null,
              endTime: null,
            }),
        duration: Number(action.payload),
      };
    }
    case bookingActionTypes.UPDATE_PICKUP_LOCATION: {
      return {
        ...state,
        ...(action.keepTimeslot
          ? {}
          : {
              date: null,
              startTime: null,
              endTime: null,
            }),
        pickupLocationId: action.payload,
      };
    }
    case bookingActionTypes.UPDATE_DROPOFF_LOCATION: {
      return {
        ...state,
        ...(action.keepTimeslot
          ? {}
          : {
              date: null,
              startTime: null,
              endTime: null,
            }),
        dropoffLocationId: action.payload,
      };
    }
    case bookingActionTypes.UPDATE_DATE: {
      return {
        ...state,
        date: action.payload,
      };
    }
    case bookingActionTypes.UPDATE_START_TIME: {
      return {
        ...state,
        startTime: action.payload,
      };
    }
    case bookingActionTypes.UPDATE_END_TIME: {
      return {
        ...state,
        endTime: action.payload,
      };
    }
    case selecetedShopIdActionTypes.SELECTED_SHOP_ID_UPDATE: {
      return {
        ...bookingReducerInitialState,
        shopId: action.payload,
        lang: state.lang,
      };
    }
    case bookingActionTypes.UPDATE_PRICE_MODEL_ID: {
      return {
        ...state,
        priceModelId: action.payload,
      };
    }
    case bookingActionTypes.UPDATE_CANCELLATION_POLICY: {
      return {
        ...state,
        cancellationPolicyId: action.payload,
      };
    }
    default: {
      return state;
    }
  }
};

export default bookingReducer;
